/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  INCOME_SOURCE_BADGE_STATUSES_MAP,
  INCOME_STATUES,
  OCROLUS_FAIL_STATUSES
} from "./constants";
import { IncomeSourceMappedStatus, NetIncomeAnalysis } from "./types";
import moment from "moment";
import { schemeCategory10 } from "d3-scale-chromatic";
import { CombinedIncome } from "./types/income";
import { Warning } from "./types/verified/common";
import { getIncomeJobName } from "./utils";
import { toMoney } from "shared/utils/currency";
import { parseDocStatus } from "views/applications-id-report/utils/parseDocStatus";
import { HugeiconsIcon } from "@hugeicons/react";
import { File02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Badge, BadgeSize, Show } from "shared/components";
import { InvalidData } from "./components/InvalidData";
import { IncomeType, VerifyMethodType } from "shared/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { BankStatementTableRowData } from "./slices/TablesSlice/components/BankStatementTable/types";

export const defaultTableColumns = [
  {
    header: "Verified income sources",
    accessor: (row) => row.source,
    widthClass: "w-full"
  },
  {
    header: "Date range",
    accessor: (row) => row.dateRange,
    widthClass: "w-full"
  },
  {
    header: "Gross monthly",
    accessor: (row) => row.grossMonthly,
    widthClass: "w-[120px] shrink-0"
  },
  {
    header: "Net monthly",
    accessor: (row) => row.netMonthly,
    widthClass: "w-[120px] shrink-0"
  }
];

export const documentTypeTableColumns = [
  {
    header: "Document",
    accessor: (row) => (
      <Tooltip>
        <TooltipTrigger>
          <div className="flex items-center gap-2">
            <HugeiconsIcon
              icon={File02Icon}
              size={20}
              stroke="1px"
              className="text-content-100"
            />
            <p className="text-content-1000 font-medium text-sm w-[120px] text-ellipsis whitespace-nowrap overflow-hidden">
              {row.asset?.asset?.filename ?? `Document #${row.index}`}
            </p>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-[370px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-content-1000-inverted">
          {row.asset?.asset?.filename ?? `Document #${row.index}`}
        </TooltipContent>
      </Tooltip>
    ),
    widthClass: "w-full"
  },
  {
    header: "Date range",
    accessor: (row) => {
      let startDate: string;
      let endDate: string;

      if (row?.verifyType === VerifyMethodType.BankStatement) {
        startDate = row?.additional_data?.bank_statement_start_date;
        endDate = row?.additional_data?.bank_statement_end_date;
      } else {
        startDate = row?.paystub_details?.pay_period_start_date;
        endDate = row?.paystub_details?.pay_period_end_date;
      }

      return (
        <p className="text-content-200 font-medium text-sm">
          {!startDate && !endDate ? (
            "-"
          ) : (
            <>
              {startDate ? (
                moment(startDate).format("ll")
              ) : (
                <InvalidData text="Invalid start date" />
              )}{" "}
              -{" "}
              {endDate ? (
                moment(endDate).format("ll")
              ) : (
                <InvalidData text="Invalid end date" />
              )}
            </>
          )}
        </p>
      );
    },
    widthClass: "w-full"
  },
  {
    header: "Gross pay",
    accessor: (row) => {
      if (row?.verifyType === VerifyMethodType.BankStatement) {
        const { totals, incomeId } = row as BankStatementTableRowData;
        const { gross_total } = (incomeId && totals?.[incomeId]) || {};

        return gross_total ? toMoney(gross_total) : "-";
      }

      return row?.total_gross_pay_amount
        ? toMoney(row?.total_gross_pay_amount)
        : "-";
    },
    widthClass: "w-[120px] shrink-0 text-right"
  },
  {
    header: "Net pay",
    accessor: (row) => {
      if (row?.verifyType === VerifyMethodType.BankStatement) {
        const { totals, incomeId } = row as BankStatementTableRowData;
        const { net_total } = (incomeId && totals?.[incomeId]) || {};

        return net_total ? toMoney(net_total) : "-";
      }

      return row.total_net_pay_amount ? toMoney(row.total_net_pay_amount) : "-";
    },
    widthClass: "w-[120px] shrink-0 text-right"
  },
  {
    header: "Status",
    accessor: (row) => {
      let isInvalidDoc = false;
      let status: "Rejected" | "Warning" | "Accepted";

      if (row.verifyType === VerifyMethodType.BankStatement)
        status = parseDocStatus(row?.raw_data?.trust_score || 0)!;
      else {
        isInvalidDoc = OCROLUS_FAIL_STATUSES.includes(
          row.status ?? row.detect_status
        );
        status = parseDocStatus(
          isInvalidDoc
            ? 0
            : (row.doc_analysis?.form_analysis ?? row.form_analysis)[0]
                ?.form_authenticity?.score || 0
        )!;
      }

      const config = INCOME_SOURCE_BADGE_STATUSES_MAP[status];

      if (config)
        return (
          <Badge
            variant={config.variant}
            size={BadgeSize.SM}
            className="items-center"
          >
            {config.title}
            <Show if={status === "Rejected"}>
              {getRejectionReason(row).title}
            </Show>
          </Badge>
        );

      return null;
    },
    widthClass: "w-full text-right"
  }
];

export function formatMonths(months: number): string {
  if (months < 12) return `${months} month${months === 1 ? "" : "s"}`;

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  const parts: string[] = [];

  if (years) parts.push(`${years} year${years > 1 ? "s" : ""}`);
  if (remainingMonths)
    parts.push(`${remainingMonths} month${remainingMonths > 1 ? "s" : ""}`);

  return parts.join(", ");
}

type MappedIncome = {
  month: string;
  total: number;
  income: {
    month: string;
    company: string;
    income: number;
    color: string;
  }[];
};

export function mapNetIncomeAnalysis(data: NetIncomeAnalysis): MappedIncome[] {
  const monthMap = new Map<string, MappedIncome>();
  const companies = Object.keys(data);
  const colorMap = Object.fromEntries(
    companies.map((company, index) => [
      company,
      schemeCategory10[index % schemeCategory10.length]
    ])
  );

  Object.entries(data).forEach(([company, months]) => {
    Object.entries(months).forEach(([month, incomeData]) => {
      let income = 0;

      if (!monthMap.has(month))
        monthMap.set(month, {
          month,
          total: 0,
          income: []
        });

      const entry = monthMap.get(month)!;

      if (typeof incomeData === "object" && "total" in incomeData) {
        income = incomeData.total;
        incomeData.sources.forEach((source) => {
          entry.income.push({
            month: source.pay_date,
            company: source.description,
            income: source.net_amount,
            color: colorMap[company]
          });
        });
      } else {
        income = incomeData;

        entry.income.push({
          month,
          company,
          income,
          color: colorMap[company]
        });
      }

      entry.total += income;
    });
  });

  return Array.from(monthMap.values()).sort((a, b) =>
    b.month.localeCompare(a.month)
  );
}

export const scrollToIncomeSource = (incomeId: number) => {
  const element = document.getElementById(
    `combined-income-section-${incomeId}`
  );
  if (element) element.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const mapIncomeSourceStatus = (
  income: CombinedIncome
): IncomeSourceMappedStatus => {
  const incomeStatusMeta = {
    incomeId: income.id,
    incomeName: getIncomeJobName(income),
    type: income.type
  };

  if (income.type === IncomeType.OfferLetter)
    return {
      ...incomeStatusMeta,
      status: "Rejected"
    };

  let rejectedDocumentsCount = 0;
  let documentsStatus: "Rejected" | "Warning" | "Accepted" | undefined;

  income.items.forEach((item) => {
    if (
      (item.income_source_type === "paystub" ||
        item.income_source_type === "w2") &&
      item.data?.document?.warnings?.length
    ) {
      item.data.document.warnings.forEach((warning: Warning) => {
        const isDocumentRejected =
          OCROLUS_FAIL_STATUSES.includes(warning.detect_status) ||
          parseDocStatus(
            warning.form_analysis[0]?.form_authenticity?.score ?? 0
          ) === "Rejected";

        if (isDocumentRejected) rejectedDocumentsCount += 1;
      });

      // If every item is rejected - the income is considered to be Rejected, otherwise - it's a Warning
      documentsStatus =
        !item.data.document.income_sources?.length &&
        item.data.document.warnings?.length === rejectedDocumentsCount
          ? "Rejected"
          : "Warning";
    }
    // No item warnings - income is Accepted
    else documentsStatus = "Accepted";
  });

  return {
    ...incomeStatusMeta,
    status: documentsStatus || INCOME_STATUES[income.status]
  };
};

const getRejectionReason = (warning: Warning) => {
  const isInvalidDocType =
    OCROLUS_FAIL_STATUSES.includes(warning.detect_status) ||
    !warning.form_analysis?.length;
  const isLowScore =
    parseDocStatus(
      isInvalidDocType
        ? 0
        : (warning.form_analysis[0]?.form_authenticity?.score ?? 0)
    ) === "Rejected";
  const verboseReason =
    warning.doc_info?.rejection_reason_description ||
    warning.doc_info?.rejection_reason ||
    "Unknown error";

  if (isInvalidDocType)
    return {
      title: " - Invalid document",
      verboseReason
    };
  else if (isLowScore)
    return {
      title: " - Potential fraud",
      verboseReason
    };
  else
    return {
      title: "",
      verboseReason
    };
};
